import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Stack,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Star as StarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import SectionHeader from "../components/SectionHeader";

interface PackageCardsProps {
  bgcolor?: string;
  onSelectPackage?: (packageType: string) => void;
}

interface PackageFeature {
  icon: string;
  label: string;
  text: string;
}

interface PackageData {
  title: string;
  badge?: string;
  badgeColor?: string;
  badgeTooltip?: string;
  cardBgColor?: string;
  accentColor?: string;
  bestFor: string;
  features: PackageFeature[];
  whatToExpect: string;
  priceFrom: string;
  ctaText: string;
  ctaVariant: "contained" | "outlined";
  isHighlighted: boolean;
  packageParam: string;
}

const packages: PackageData[] = [
  {
    title: "Standard",
    cardBgColor: "rgba(158, 158, 158, 0.05)",
    accentColor: "#9E9E9E",
    bestFor: "Well-maintained units and budget-focused turnovers",
    features: [
      { icon: "📋", label: "Scope of Work:", text: "Standard turnover checklist" },
      { icon: "🧹", label: "Cleaning Standard:", text: "Professional turnover process" },
      { icon: "🔍", label: "Quality Control:", text: "Standard quality check" },
      { icon: "⏱️", label: "Time Allocation:", text: "Standard" },
      { icon: "📅", label: "Scheduling:", text: "Standard" },
      { icon: "📸", label: "Reporting:", text: "Photo reporting" },
    ],
    whatToExpect:
      "Reliable and efficient guest-ready turnovers",
    priceFrom: "From $90",
    ctaText: "Get Quote",
    ctaVariant: "outlined",
    isHighlighted: false,
    packageParam: "standard",
  },
  {
    title: "Professional",
    badge: "Recommended",
    badgeColor: "#1976d2",
    badgeTooltip: "Best balance of cost & reliability",
    cardBgColor: "rgba(25, 118, 210, 0.05)",
    accentColor: "#1976d2",
    bestFor: "The best balance of quality and value for most hosts",
    features: [
      { icon: "📋", label: "Scope of Work:", text: "Standard turnover checklist + additional tasks" },
      { icon: "🧹", label: "Cleaning Standard:", text: "Enhanced attention to detail" },
      { icon: "🔍", label: "Quality Control:", text: "Enhanced quality inspection" },
      { icon: "⏱️", label: "Time Allocation:", text: "Extended" },
      { icon: "📅", label: "Scheduling:", text: "Priority" },
      { icon: "📸", label: "Reporting:", text: "Enhanced photo reporting" },
    ],
    whatToExpect:
      "Greater attention to detail and more areas covered for better guest experiences",
    priceFrom: "From $110",
    ctaText: "Get Pro Quote",
    ctaVariant: "contained",
    isHighlighted: true,
    packageParam: "professional",
  },
  {
    title: "White-Glove",
    badge: "Premium",
    badgeColor: "#9C27B0",
    badgeTooltip: "Maximum quality control + priority scheduling",
    cardBgColor: "rgba(156, 39, 176, 0.05)",
    accentColor: "#9C27B0",
    bestFor: "For luxury properties or hosts with custom requirements",
    features: [
      { icon: "📋", label: "Scope of Work:", text: "Customized property checklist" },
      { icon: "🧹", label: "Cleaning Standard:", text: "Tailored cleaning process" },
      { icon: "🔍", label: "Quality Control:", text: "Tailored quality inspection" },
      { icon: "⏱️", label: "Time Allocation:", text: "Maximum" },
      { icon: "📅", label: "Scheduling:", text: "Guaranteed" },
      { icon: "📸", label: "Reporting:", text: "Customized reporting" },
    ],
    whatToExpect:
      "Fully customized service tailored to your property's standards",
    priceFrom: "From $150",
    ctaText: "Get White-Glove Quote",
    ctaVariant: "contained",
    isHighlighted: false,
    packageParam: "whiteglove",
  },
];

interface PackageCardProps {
  title: string;
  badge?: string;
  badgeColor?: string;
  badgeTooltip?: string;
  cardBgColor?: string;
  accentColor?: string;
  bestFor: string;
  features: PackageFeature[];
  whatToExpect: string;
  priceFrom: string;
  onRequestQuote: () => void;
  ctaText: string;
  ctaVariant: "contained" | "outlined";
  isHighlighted: boolean;
}

function PackageCard({
  title,
  badge,
  badgeColor = "#FF9800",
  badgeTooltip,
  cardBgColor,
  accentColor,
  bestFor,
  features,
  whatToExpect,
  priceFrom,
  onRequestQuote,
  ctaText,
  ctaVariant,
  isHighlighted,
}: PackageCardProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getBackgroundColor = () => {
    if (cardBgColor) return cardBgColor;
    if (isHighlighted) return "rgba(25, 118, 210, 0.03)";
    return "background.paper";
  };

  const getBorder = () => {
    if (isHighlighted) return `2.5px solid ${accentColor || "#1976d2"}`;
    if (title === "White-Glove") return `2px solid ${accentColor || "#9C27B0"}`;
    if (title === "Standard") return `2px solid ${accentColor || "#9E9E9E"}`;
    return "1px solid #eee";
  };

  return (
    <Box
      sx={{
        p: { xs: 2.5, md: 3 },
        border: getBorder(),
        borderRadius: "16px",
        boxShadow: isHighlighted
          ? "0 8px 24px rgba(25, 118, 210, 0.18)"
          : "0 2px 8px rgba(0, 0, 0, 0.04)",
        bgcolor: getBackgroundColor(),
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        transition: "all 0.3s ease",
        transform: isHighlighted
          ? { xs: "scale(1)", md: "scale(1.05)" }
          : "scale(1)",
        "&:hover": {
          boxShadow: isHighlighted
            ? "0 10px 28px rgba(25, 118, 210, 0.22)"
            : "0 4px 16px rgba(0, 0, 0, 0.08)",
          transform: isHighlighted
            ? { xs: "translateY(-2px)", md: "scale(1.05) translateY(-2px)" }
            : "translateY(-2px)",
        },
      }}
    >
      {badge && (
        <Tooltip
          title={badgeTooltip || ""}
          enterDelay={400}
          arrow
          placement="top"
          disableHoverListener={isMobile || !badgeTooltip}
          disableTouchListener
        >
          <Chip
            icon={<StarIcon sx={{ fontSize: 14 }} />}
            label={badge}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              bgcolor: badgeColor,
              color: "white",
              fontWeight: "bold",
              fontSize: "0.7rem",
            }}
          />
        </Tooltip>
      )}

      <Typography
        variant="h5"
        fontWeight="bold"
        sx={{
          mb: 1,
          fontSize: { xs: "1.25rem", md: "1.35rem" },
          pr: badge ? 5 : 0,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="h6"
        color="primary"
        fontWeight="bold"
        sx={{
          mb: 1,
          fontSize: { xs: "1rem", md: "1.05rem" },
        }}
      >
        {priceFrom}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 2.5,
          fontStyle: "italic",
          fontSize: "0.9rem",
        }}
      >
        {bestFor}
      </Typography>

      <Stack spacing={1.25} sx={{ mb: 2.5, flexGrow: 1 }}>
        {features.map((feature, index) => (
          <Stack
            key={index}
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
          >
            <Typography
              sx={{
                mt: 0.15,
                fontSize: "0.95rem",
                lineHeight: 1.2,
                flexShrink: 0,
              }}
            >
              {feature.icon}
            </Typography>
            <Box>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  lineHeight: 1.5,
                  color: "#333",
                  fontSize: "0.875rem",
                  textAlign: "left",
                  fontWeight: 700,
                  display: "block",
                }}
              >
                {feature.label}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.5,
                  color: "#333",
                  fontSize: "0.875rem",
                  textAlign: "left",
                }}
              >
                {feature.text}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Box
        sx={{
          p: 1.5,
          bgcolor: "rgba(76, 175, 80, 0.04)",
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontWeight: 600,
            mb: 0.5,
            fontSize: "0.7rem",
            letterSpacing: 0.5,
          }}
        >
          WHAT TO EXPECT
        </Typography>
        <Typography
          variant="body2"
          sx={{
            lineHeight: 1.5,
            color: "#333",
            fontSize: "0.85rem",
          }}
        >
          {whatToExpect}
        </Typography>
      </Box>

      <Button
        variant={ctaVariant}
        color="primary"
        fullWidth
        onClick={onRequestQuote}
        sx={{
          height: { xs: 44, md: 48 },
          fontWeight: "bold",
          fontSize: { xs: "0.9rem", md: "1rem" },
          whiteSpace: "nowrap",
        }}
      >
        {ctaText}
      </Button>
    </Box>
  );
}

export default function PackageCards({ bgcolor, onSelectPackage }: PackageCardsProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(1); // Start with Professional
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    // Scroll to Professional card on mount for mobile
    if (isMobile && scrollContainerRef.current) {
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const container = scrollContainerRef.current;
          const firstCard = container.children[0] as HTMLElement;
          if (firstCard) {
            const cardWidth = firstCard.offsetWidth;
            const gap = 16;
            container.scrollLeft = (cardWidth + gap) * 1;
          }
        }
      }, 100);
    }
  }, [isMobile]);

  const handleRequestQuote = (packageType: string) => {
    if (onSelectPackage) {
      onSelectPackage(packageType);
    }
  };

  const scrollToCard = (direction: "prev" | "next") => {
    if (scrollContainerRef.current && !isScrolling) {
      setIsScrolling(true);
      const container = scrollContainerRef.current;
      const firstCard = container.children[0] as HTMLElement;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 16;
        const scrollAmount = cardWidth + gap;
        
        let newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
        
        // Clamp index to valid range
        newIndex = Math.max(0, Math.min(mobilePackages.length - 1, newIndex));
        
        container.scrollTo({
          left: scrollAmount * newIndex,
          behavior: "smooth",
        });
        
        setTimeout(() => {
          setCurrentIndex(newIndex);
          setIsScrolling(false);
        }, 300);
      }
    }
  };

  const handleScroll = () => {
    if (scrollContainerRef.current && !isScrolling) {
      const container = scrollContainerRef.current;
      const firstCard = container.children[0] as HTMLElement;
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        const gap = 16;
        const scrollPosition = container.scrollLeft;
        const index = Math.round(scrollPosition / (cardWidth + gap));
        setCurrentIndex(index);
      }
    }
  };

  // Reorder packages for mobile: Standard, Professional, White-Glove
  const mobilePackages = [
    packages.find(p => p.title === "Standard")!,
    packages.find(p => p.title === "Professional")!,
    packages.find(p => p.title === "White-Glove")!,
  ];

  return (
    <Box sx={{ width: "100%", bgcolor }}>
      <Container
        component="section"
        maxWidth="lg"
      sx={{
        maxWidth: "1000px !important",
        pt: { xs: 4, md: 7 },
        pb: { xs: 3, md: 6 },
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <SectionHeader
        title="AirBnB Cleaning Packages"
        subtitle="Choose the level of care and quality control that fits your hosting style."
      />

      {/* Mobile Carousel */}
      {isMobile ? (
        <Box sx={{ position: "relative", mb: { xs: 4, md: 6 } }}>
          {/* Carousel Container */}
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              display: "flex",
              overflowX: "auto",
              scrollSnapType: "x mandatory",
              scrollBehavior: "smooth",
              gap: 2,
              pb: 2,
              mx: -0.5,
              px: 0.5,
              "&::-webkit-scrollbar": {
                display: "none",
              },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {mobilePackages.map((pkg) => (
              <Box
                key={pkg.title}
                sx={{
                  minWidth: "calc(100% - 80px)",
                  maxWidth: "calc(100% - 80px)",
                  scrollSnapAlign: "center",
                  flexShrink: 0,
                  mt: 1
                }}
              >
                <PackageCard
                  {...pkg}
                  onRequestQuote={() => handleRequestQuote(pkg.packageParam)}
                />
              </Box>
            ))}
          </Box>

          {/* Navigation Arrows */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <Button
              onClick={() => scrollToCard("prev")}
              disabled={currentIndex === 0}
              sx={{
                minWidth: 40,
                height: 40,
                borderRadius: "50%",
                p: 0,
              }}
            >
              <ChevronLeftIcon />
            </Button>

            {/* Dots Indicator */}
            <Stack direction="row" spacing={1}>
              {mobilePackages.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: currentIndex === index ? "primary.main" : "grey.300",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </Stack>

            <Button
              onClick={() => scrollToCard("next")}
              disabled={currentIndex === mobilePackages.length - 1}
              sx={{
                minWidth: 40,
                height: 40,
                borderRadius: "50%",
                p: 0,
              }}
            >
              <ChevronRightIcon />
            </Button>
          </Box>
        </Box>
      ) : (
        /* Desktop Grid */
        <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 4, md: 6 } }}>
          {packages.map((pkg, index) => {
            // Set order: Desktop (Standard > Professional > White-Glove)
            const getOrder = () => {
              if (pkg.title === "Standard") return 1;
              if (pkg.title === "Professional") return 2;
              if (pkg.title === "White-Glove") return 3;
              return 1;
            };

            return (
              <Grid item xs={12} md={4} key={index} sx={{ order: getOrder() }}>
                <PackageCard
                  {...pkg}
                  onRequestQuote={() => handleRequestQuote(pkg.packageParam)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Pricing Note */}
        {/* <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: "center",
            fontStyle: "italic",
            fontSize: "0.875rem",
          }}
        >
          All service levels use the same checklist. Differences are time allocation, cleaner experience, and quality control.
        </Typography> */}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: "center",
            fontStyle: "italic",
            fontSize: "0.875rem",
          }}
        >
          Pricing depends on unit size, condition, task requirements, and selected service level.
        </Typography>

    </Container>
    </Box>
  );
}
